import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Check, Copy } from "lucide-react";
import api, { apiErr } from "@/lib/api";
import { Field, useValidatedForm, isBlank, isDomain } from "@/components/app/FormKit";
import { btnPrimary, cardCls, pageSub, pageTitle } from "@/components/app/ui";

const normalize = (d) => d.trim().toLowerCase().replace(/^https?:\/\//, "").replace(/\/.*$/, "");

export default function Domain() {
  const [store, setStore] = useState(null);
  const [records, setRecords] = useState([]);
  const [busy, setBusy] = useState(false);

  const validate = useCallback((f) => {
    const e = {};
    const d = normalize(f.domain);
    if (isBlank(f.domain)) e.domain = "Enter your domain, e.g. www.mystore.com";
    else if (/\s/.test(f.domain.trim())) e.domain = "Domain cannot contain spaces.";
    else if (!isDomain(d)) e.domain = "Enter a valid domain like www.mystore.com (no paths or special characters).";
    else if (d.endsWith("sell24x7.com")) e.domain = "Use your own domain, not a sell24x7.com address.";
    return e;
  }, []);
  const form = useValidatedForm({ domain: "" }, validate);

  const load = useCallback(() => Promise.all([api.get("/vendor/store"), api.get("/vendor/domain")]).then(([{ data }, { data: d }]) => { setStore(data); setRecords(d.dns_records || []); form.reset({ domain: data.custom_domain || "" }); }), []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const connect = async () => {
    if (!form.check()) return;
    setBusy(true);
    try {
      const domain = normalize(form.values.domain);
      const { data } = await api.post("/vendor/domain", { domain });
      setRecords(data.dns_records || []);
      toast.success(`${domain} added. Point its DNS to the record below, then verify.`);
      load();
    } catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const verify = async () => {
    setBusy(true);
    try { const { data } = await api.post("/vendor/domain/verify"); toast.success(data.message || "Domain verified and connected!"); load(); }
    catch (err) { toast.error(apiErr(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const locked = store && !store.plan_info?.custom_domain;

  return (
    <div className="space-y-4" data-testid="vendor-domain">
      <div>
        <h1 className={pageTitle}>Custom Domain</h1>
        <p className={pageSub}>Connect your own domain to your storefront.</p>
      </div>

      {locked ? (
        <div className="rounded-xl bg-orange-50 border border-orange-200 p-4 text-[13px] text-orange-700">
          Custom domains are not included in your current plan (<b>{store?.plan_info?.name}</b>). Upgrade to a plan that includes them.
          <a href="/vendor/plan" className="underline font-semibold ml-1">Upgrade now</a>.
        </div>
      ) : (
        <div className={`${cardCls} p-5 max-w-3xl`}>
          <div className="text-[13px] text-slate-500 mb-3">Current storefront: <span className="font-mono text-orange-600">sell24x7.com/store/{store?.slug}</span></div>
          {store?.custom_domain && (
            <div className="flex items-center gap-2 mb-3 text-[13px]">
              <span className="font-mono">{store.custom_domain}</span>
              <span className={`text-[11px] px-2 py-0.5 rounded-md border font-medium ${store.domain_status === "connected" ? "bg-green-50 text-green-700 border-green-200" : "bg-orange-50 text-orange-700 border-orange-200"}`}>{store.domain_status}</span>
            </div>
          )}
          <div className="flex gap-3 items-start">
            <Field className="flex-1" label="Your domain" required t="domain-input" placeholder="www.mystore.com" inputClassName="font-mono"
              hint="Enter the domain only — no http:// or paths." {...form.bind("domain")} />
            <button onClick={connect} disabled={busy} data-testid="domain-connect" className={`${btnPrimary} mt-[22px]`}>{busy ? "Working…" : "Connect"}</button>
          </div>

          {store?.custom_domain && records.length > 0 && (
            <div className="mt-4 rounded-lg bg-slate-50 border border-slate-200 p-4">
              <div className="text-[13px] font-semibold mb-2">DNS configuration</div>
              <p className="text-[12px] text-slate-500 mb-2">Add {records.length > 1 ? "these records" : "this record"} at your domain registrar (where you bought the domain):</p>
              <div className="space-y-2">
                {records.map((r, i) => (
                  <div key={i} className="font-mono text-[12px] text-slate-600 rounded-md bg-white border border-slate-200 px-3 py-2">
                    <div><span className="text-slate-500">Type:</span> {r.type}</div>
                    <div><span className="text-slate-500">Name:</span> {r.name}</div>
                    <div className="flex items-center gap-2"><span className="text-slate-500">Value:</span> {r.value}
                      <button onClick={() => { navigator.clipboard.writeText(r.value); toast.success("Copied to clipboard."); }} className="text-slate-500 hover:text-blue-600" aria-label="Copy value"><Copy size={13} /></button>
                    </div>
                    {r.note && <div className="font-sans text-[11px] text-slate-500 mt-1">{r.note}</div>}
                  </div>
                ))}
              </div>
              {store?.domain_status !== "connected" && (
                <button onClick={verify} disabled={busy} data-testid="domain-verify" className={`${btnPrimary} mt-3`}><Check size={14} /> Verify & connect</button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
